import NotificationDetailsModal, {
  getServiceInterestNotificationMetadata,
} from "@/components/notifications/NotificationDetailsModal";
import { NotificationType } from "@/interfaces";
import { notificationService, useNotifications, useUnreadCount } from "@/services/notificationService";
import { useUserStore } from "@/stores/userStore";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dropdown, Empty, List, message, Spin, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Bell, CheckCheck, Eye } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

dayjs.extend(relativeTime);

const { Text } = Typography;

/**
 * NotificationDropdown component
 * Displays a bell icon with unread count badge and dropdown of latest 10 notifications
 */
const NotificationDropdown = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentCompany = useUserStore((state) => state.currentCompany);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationType | null>(null);

  const { data: notificationsData, isLoading } = useNotifications(1, 10, undefined, currentCompany?.id, dropdownOpen);
  const { data: unreadCountData } = useUnreadCount(currentCompany?.id);

  // Get unread count from dedicated notification API
  const unreadCount = unreadCountData?.unreadCount || 0;
  const notifications = notificationsData?.data || [];

  /**
   * Handle notification click - mark as read and redirect if URL exists
   */
  const handleNotificationClick = async (notification: NotificationType) => {
    if (!notification.isRead) {
      try {
        await notificationService.markAsRead(notification.id);
        // Invalidate queries to refetch data
        await queryClient.invalidateQueries({ queryKey: ["notifications"] });
      } catch {
        message.error("Failed to mark notification as read");
      }
    }

    setDropdownOpen(false); // Close dropdown

    if (getServiceInterestNotificationMetadata(notification)) {
      setSelectedNotification(notification);
      return;
    }

    if (notification.redirectUrl) {
      navigate(notification.redirectUrl);
    }
  };

  /**
   * Handle mark all as read
   */
  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMarkingAllAsRead(true);
    try {
      const result = await notificationService.markAllAsRead();
      message.success(`${result.count} notification(s) marked as read`);
      // Invalidate queries to refetch data
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch {
      message.error("Failed to mark all notifications as read");
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  /**
   * Get notification icon based on type
   */
  const getNotificationIcon = (type: NotificationType["type"]) => {
    const iconClass = "h-4 w-4";
    switch (type) {
      case "Invoice":
        return <span className={iconClass}>💰</span>;
      case "Payment":
        return <span className={iconClass}>💳</span>;
      case "Client":
        return <span className={iconClass}>👤</span>;
      case "Alert":
        return <span className={iconClass}>⚠️</span>;
      case "System":
        return <span className={iconClass}>⚙️</span>;
      default:
        return <span className={iconClass}>ℹ️</span>;
    }
  };

  /**
   * Dropdown menu content
   */
  const dropdownContent = (
    <div className="w-96 max-h-[500px] overflow-hidden flex flex-col bg-white border border-gray-100 rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <Text strong className="text-base">
          Notifications
        </Text>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckCheck className="h-4 w-4" />}
            onClick={handleMarkAllAsRead}
            loading={isMarkingAllAsRead}
          >
            Mark all as read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="flex justify-center items-center p-8">
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" className="py-8" />
        ) : (
          <List
            dataSource={notifications}
            renderItem={(notification) => (
              <List.Item
                key={notification.id}
                className={`cursor-pointer hover:bg-gray-50 transition-colors px-2! ${
                  !notification.isRead ? "bg-blue-50" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
                aria-label={`${notification.isRead ? "Read" : "Unread"} notification: ${notification.title}`}
              >
                <div className="flex gap-3 w-full">
                  <div className="shrink-0 mt-1">{getNotificationIcon(notification.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <Text strong className={`text-sm ${!notification.isRead ? "text-blue-600" : ""}`}>
                        {notification.title}
                      </Text>
                      {!notification.isRead && <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-1" />}
                    </div>
                    <Text className="text-xs text-gray-600 block mt-1 line-clamp-2">{notification.message}</Text>
                    <Text className="text-xs text-gray-400 block mt-1">
                      {dayjs(notification.createdAt || notification.date).fromNow()}
                    </Text>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 text-center">
          <Button
            type="link"
            icon={<Eye className="h-4 w-4" />}
            onClick={(e) => {
              e.stopPropagation();
              navigate("/notifications");
              setDropdownOpen(false);
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            View all notifications
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Dropdown
        popupRender={() => dropdownContent}
        trigger={["click"]}
        placement="bottomRight"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
      >
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <Button type="text" className="relative" icon={<Bell className="h-5 w-5" />} />
        </Badge>
      </Dropdown>
      <NotificationDetailsModal
        open={!!selectedNotification}
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
      />
    </>
  );
};

export default NotificationDropdown;
